import { useState, useEffect, useRef } from 'react';
import { Save, Loader2, Check, ListMusic } from 'lucide-react';

interface SaveBarProps {
  hasChanges: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  onSaveAndNavigate?: () => void;
}

/**
 * Reusable floating save bar — appears at the bottom of the viewport.
 *
 * States:
 *   hasChanges=true  → "Save Changes" + optional "Discard" buttons
 *   saving=true      → Spinner
 *   just saved        → "Saved ✓" (auto-fades after 3 seconds)
 *   idle              → hidden
 */
export default function SaveBar({ hasChanges, saving, onSave, onDiscard, onSaveAndNavigate }: SaveBarProps) {
  const [recentlySaved, setRecentlySaved] = useState(false);
  const prevSaving = useRef(false);

  // Detect when saving finishes → show "Saved" briefly
  useEffect(() => {
    if (prevSaving.current && !saving && !hasChanges) {
      setRecentlySaved(true);
      const timer = setTimeout(() => setRecentlySaved(false), 3000);
      return () => clearTimeout(timer);
    }
    prevSaving.current = saving;
  }, [saving, hasChanges]);

  const visible = hasChanges || saving || recentlySaved;
  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)] animate-in slide-in-from-bottom duration-300">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        {recentlySaved && !hasChanges ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium">
            <Check className="w-4 h-4" />
            Saved
          </span>
        ) : (
          <>
            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              You have unsaved changes
            </span>
            <div className="flex items-center gap-2">
              {onDiscard && (
                <button
                  type="button"
                  onClick={onDiscard}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Discard
                </button>
              )}
              {onSaveAndNavigate && (
                <button
                  type="button"
                  onClick={onSaveAndNavigate}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors inline-flex items-center"
                  disabled={saving}
                >
                  <ListMusic className="w-4 h-4 mr-1.5" />
                  Save & Queue
                </button>
              )}
              <button
                type="button"
                onClick={onSave}
                className="btn btn-primary text-sm"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
